import { useKeycloak } from '@react-keycloak/web';
import { useCallback, useMemo } from 'react';

import axios from 'axios';

import { AdvertisingPackages } from '@/api/AdvertisingPackages';
import { Companies } from '@/api/Companies';
import { Leasing } from '@/api/Leasing';
import { Locations } from '@/api/Locations';
import { MediaPlans } from '@/api/MediaPlans';
import { Orders } from '@/api/Orders';
import { Organizations } from '@/api/Organizations';
import { Services } from '@/api/Services';
import { Spaces } from '@/api/Spaces';
import { Users } from '@/api/Users';

import { optionalApiMethod } from './optionalApiMethod';
import useUploadProgressService from './uploadProgress.service';

/**
 * Self-contained resource-download methods for shared/lib's useResourceImage.
 *
 * These are copied VERBATIM from the domain services (tasks/spaces/orders/locations/
 * packages/mediaPlans/companies/users) so behavior is byte-identical. They are
 * DUPLICATED rather than moved because the same methods are still consumed by feature
 * code — and shared/ may not import features/. This is the anti-corruption boundary
 * that lets those domain services relocate into their features without shared→feature edges.
 */
const useResourceDownloads = () => {
  const { keycloak } = useKeycloak();

  const servicesApi = useMemo(
    () =>
      new Services({
        headers: { Authorization: `Bearer ${keycloak.token}`, 'Content-Type': 'application/json' },
      }),
    [keycloak.token],
  );
  const advertisingSpacesApi = useUploadProgressService(Spaces);
  const ordersApi = useUploadProgressService(Orders);
  const locationApi = useUploadProgressService(Locations);
  const leasingApi = useMemo(
    () => new Leasing({ headers: { Authorization: `Bearer ${keycloak.token}` } }),
    [keycloak.token],
  );
  const apiInsetance = useMemo(
    () =>
      new AdvertisingPackages({
        headers: { Authorization: `Bearer ${keycloak.token}`, 'Content-Type': 'application/json' },
      }),
    [keycloak.token],
  );
  const mediaPlansApi = useUploadProgressService(MediaPlans);
  const companiesApi = useUploadProgressService(Companies);
  const organizationsApi = useUploadProgressService(Organizations);
  const usersApi = useUploadProgressService(Users);

  const downloadServiceTaskDocument = useCallback(
    async (taskId: string, documentId: string, signal?: AbortSignal): Promise<Blob> => {
      const response = await servicesApi.getServiceTaskDocument(taskId, documentId, {
        format: 'blob',
        signal,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
      } as any);
      return response.data;
    },
    [servicesApi],
  );

  const downloadCampaignMonitorImage = useCallback(
    async (taskId: string, documentId: string, signal?: AbortSignal): Promise<Blob> => {
      const response = await servicesApi.downloadCampaignImage(taskId, documentId, {
        format: 'blob',
        signal,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
      } as any);
      return response.data;
    },
    [servicesApi],
  );

  const downloadServiceTaskLayoutSketch = useCallback(
    async (taskId: string, documentId: string, signal?: AbortSignal): Promise<Blob> => {
      const response = await servicesApi.downloadServiceTaskLayoutSketch(taskId, documentId, {
        format: 'blob',
        signal,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
      } as any);
      return response.data;
    },
    [servicesApi],
  );

  const downloadServiceTaskLayoutSketchOriginal = useCallback(
    async (taskId: string, documentId: string, signal?: AbortSignal): Promise<Blob> => {
      const downloadOriginal = optionalApiMethod<
        (
          taskId: string,
          documentId: string,
          params: { format: 'blob'; signal?: AbortSignal },
        ) => Promise<{ data: Blob }>
      >(servicesApi, 'downloadServiceTaskLayoutSketchOriginal');
      if (!downloadOriginal) return downloadServiceTaskLayoutSketch(taskId, documentId, signal);

      const response = await downloadOriginal(taskId, documentId, { format: 'blob', signal });
      return response.data;
    },
    [servicesApi, downloadServiceTaskLayoutSketch],
  );

  const downloadArtworkMatchFile = useCallback(
    async (taskId: string, documentId: string, signal?: AbortSignal): Promise<Blob> => {
      const response = await servicesApi.downloadArtworkMatchFile(taskId, documentId, {
        format: 'blob',
        signal,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
      } as any);
      return response.data;
    },
    [servicesApi],
  );

  const downloadSpaceDocumentById = useCallback(
    async (spaceId: string, documentId: string, signal?: AbortSignal): Promise<Blob> => {
      const { data } = await advertisingSpacesApi.downloadSpaceDocument1(spaceId, documentId, {
        format: 'blob',
        signal,
      });
      return data;
    },
    [advertisingSpacesApi],
  );

  const downloadSpaceThumbnail = useCallback(
    async (spaceId: string, documentId: string, signal?: AbortSignal): Promise<Blob> => {
      const { data } = await advertisingSpacesApi.downloadSpaceDocument(spaceId, documentId, {
        format: 'blob',
        signal,
      });
      return data;
    },
    [advertisingSpacesApi],
  );

  const downloadSpaceSupportPicture = useCallback(
    async (spaceId: string, documentId: string, signal?: AbortSignal): Promise<Blob> => {
      const { data } = await advertisingSpacesApi.downloadSupportPictures(spaceId, documentId, {
        format: 'blob',
        signal,
      });
      return data;
    },
    [advertisingSpacesApi],
  );

  const downloadSpaceLayoutSketch = useCallback(
    async (spaceId: string, documentId: string, signal?: AbortSignal): Promise<Blob> => {
      const { data } = await advertisingSpacesApi.downloadSpaceLayoutSketches(spaceId, documentId, {
        format: 'blob',
        signal,
      });
      return data;
    },
    [advertisingSpacesApi],
  );

  const downloadIndividualSupportNamePicture = useCallback(
    async (url: string, signal?: AbortSignal) => {
      return axios.get(`${import.meta.env.VITE_API_URL}${url}`, {
        responseType: 'blob',
        headers: { Authorization: `Bearer ${keycloak.token}` },
        signal,
      });
    },
    [keycloak.token],
  );

  const downloadOrderDocumentById = useCallback(
    async (orderId: string, documentId: string, signal?: AbortSignal) => {
      const { data } = await ordersApi.downloadOrderDocument(orderId, documentId, {
        format: 'blob',
        signal,
      });
      return data;
    },
    [ordersApi],
  );

  const downloadOrderDocumentImageById = useCallback(
    async (orderId: string, documentId: string, signal?: AbortSignal) => {
      const { data } = await ordersApi.getLayoutSketchToImage(orderId, documentId, {
        format: 'blob',
        signal,
      });
      return data;
    },
    [ordersApi],
  );

  const downloadBookingArtworkMatchFile = useCallback(
    async (
      orderId: string,
      bookingId: string,
      documentId: string,
      signal?: AbortSignal,
    ): Promise<Blob> => {
      const response = await ordersApi.downloadArtworkMatchFile1(orderId, bookingId, documentId, {
        format: 'blob',
        signal,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
      } as any);
      return response.data;
    },
    [ordersApi],
  );

  const downloadBookingCampaignMonitorFile = useCallback(
    async (
      orderId: string,
      bookingId: string,
      documentId: string,
      signal?: AbortSignal,
    ): Promise<Blob> => {
      const response = await ordersApi.downloadBookingCampaignMonitorFile(
        orderId,
        bookingId,
        documentId,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        { format: 'blob', signal } as any,
      );
      return response.data;
    },
    [ordersApi],
  );

  const downloadDocumentById = useCallback(
    async (locationId: string, documentId: string, signal?: AbortSignal): Promise<Blob> => {
      const { data } = await locationApi.downloadLocationDocument(locationId, documentId, {
        format: 'blob',
        signal,
      });
      return data;
    },
    [locationApi],
  );

  const downloadFloorMaps = useCallback(
    async (
      locationId: string,
      documentId: string,
      signal?: AbortSignal | undefined,
    ): Promise<File> => {
      const { data } = await locationApi.downloadFloorMaps(locationId, documentId, undefined, {
        signal,
        format: 'blob',
      });

      return data;
    },
    [locationApi],
  );

  const downloadKioskPicture = useCallback(
    async (kioskId: string, documentId: string, signal?: AbortSignal): Promise<Blob> => {
      const { data } = await leasingApi.downloadKioskPicture(kioskId, documentId, {
        format: 'blob',
        signal,
      });
      return data as Blob;
    },
    [leasingApi],
  );

  const downloadRentalContract = useCallback(
    async (
      kioskId: string,
      rentalId: string,
      documentId: string,
      signal?: AbortSignal,
    ): Promise<Blob> => {
      const { data } = await leasingApi.downloadRentalContract(kioskId, rentalId, documentId, {
        format: 'blob',
        signal,
      });
      return data as Blob;
    },
    [leasingApi],
  );

  const downloadRentalAuthorization = useCallback(
    async (
      kioskId: string,
      rentalId: string,
      documentId: string,
      signal?: AbortSignal,
    ): Promise<Blob> => {
      const { data } = await leasingApi.downloadRentalAuthorization(kioskId, rentalId, documentId, {
        format: 'blob',
        signal,
      });
      return data as Blob;
    },
    [leasingApi],
  );

  const downloadAdvertisingPackageDocument = useCallback(
    async (
      advertisingPackageId: string,
      documentId: string,
      signal?: AbortSignal,
    ): Promise<Blob> => {
      const { data } = await apiInsetance.downloadAdvertisingPackageDocument(
        advertisingPackageId,
        documentId,
        { format: 'blob', signal },
      );
      return data as Blob;
    },
    [apiInsetance],
  );

  const downloadPackageSupportPicture = useCallback(
    async (
      advertisingPackageId: string,
      documentId: string,
      signal?: AbortSignal,
    ): Promise<Blob> => {
      const { data } = await apiInsetance.downloadPackageSupportPictures(
        advertisingPackageId,
        documentId,
        { format: 'blob', signal },
      );
      return data as Blob;
    },
    [apiInsetance],
  );

  const downloadMediaPlanDocumentById = useCallback(
    async (planId: string, documentId: string, signal?: AbortSignal): Promise<Blob> => {
      const { data } = await mediaPlansApi.downloadMediaPlanDocument(planId, documentId, {
        format: 'blob',
        signal,
      });
      return data;
    },
    [mediaPlansApi],
  );

  const downloadMediaPlanBookingArtworkMatchFile = useCallback(
    async (
      planId: string,
      bookingId: string,
      documentId: string,
      signal?: AbortSignal,
    ): Promise<Blob> => {
      const response = await mediaPlansApi.downloadArtworkMatchFile2(
        planId,
        bookingId,
        documentId,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        { format: 'blob', signal } as any,
      );
      return response.data;
    },
    [mediaPlansApi],
  );

  const downloadCompanyDocument = useCallback(
    async (companyId: string, documentId: string, signal?: AbortSignal): Promise<Blob> => {
      const { data } = await companiesApi.downloadCompanyDocument(
        companyId,
        documentId,
        undefined,
        {
          format: 'blob',
          signal,
        },
      );

      return data;
    },
    [companiesApi],
  );

  const downloadOrganizationDocument = useCallback(
    async (organizationId: string, documentId: string, signal?: AbortSignal): Promise<Blob> => {
      const { data } = await organizationsApi.downloadOrganizationDocument(
        organizationId,
        documentId,
        { format: 'blob', signal },
      );
      return data;
    },
    [organizationsApi],
  );

  const getUserAvatar = useCallback(
    async (userId: string, signal?: AbortSignal | undefined): Promise<Blob> => {
      const { data } = await usersApi.getUserAvatar(userId, { signal, format: 'blob' });
      return data;
    },
    [usersApi],
  );

  const getCurrentUserAvatar = useCallback(
    async (signal: AbortSignal | undefined): Promise<Blob> => {
      const { data } = await usersApi.getCurrentUserAvatar({ signal, format: 'blob' });
      return data;
    },
    [usersApi],
  );

  return {
    downloadServiceTaskDocument,
    downloadCampaignMonitorImage,
    downloadServiceTaskLayoutSketch,
    downloadServiceTaskLayoutSketchOriginal,
    downloadArtworkMatchFile,
    downloadSpaceDocumentById,
    downloadSpaceThumbnail,
    downloadSpaceSupportPicture,
    downloadSpaceLayoutSketch,
    downloadIndividualSupportNamePicture,
    downloadOrderDocumentById,
    downloadOrderDocumentImageById,
    downloadBookingArtworkMatchFile,
    downloadBookingCampaignMonitorFile,
    downloadDocumentById,
    downloadFloorMaps,
    downloadKioskPicture,
    downloadRentalContract,
    downloadRentalAuthorization,
    downloadAdvertisingPackageDocument,
    downloadPackageSupportPicture,
    downloadMediaPlanDocumentById,
    downloadMediaPlanBookingArtworkMatchFile,
    downloadCompanyDocument,
    downloadOrganizationDocument,
    getUserAvatar,
    getCurrentUserAvatar,
  };
};

export default useResourceDownloads;
